import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { ADPResourceService } from 'digital-core';
import { PreInitSettingsService } from './settings';
import { StorageService } from './storage.service';
import { AppConstants } from '../constants';
import { AFXAddon } from '../../model/addons/afx-addon.dto';
import { AfxRenewalResponse, AfxRenewalConsentUpdateResponse } from '../../model/addons/afx-renewal.dto';
import { AfxSessionInitResponse } from '../../model/addons/afx-session.dto';
import { CacheEntry } from '../../model/cache-entry/cache-entry';

@Injectable({
  providedIn: 'root',
})
export class AFXService {
  private readonly middlewareUrl: string;
  private readonly accountPath: string;

  private readonly preInitSettingsService = inject(PreInitSettingsService);
  private readonly http = inject(HttpClient);
  private readonly storageService = inject(StorageService);
  private readonly resourceService = inject(ADPResourceService);

  private readonly DEFAULT_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  private cacheDuration: number = this.DEFAULT_CACHE_DURATION;

  constructor() {
    this.middlewareUrl = this.preInitSettingsService.getSettings().middleWare.middlewareUrl;
    this.accountPath = this.preInitSettingsService.getSettings().middleWare.accountPath;
  }

  // Mirrors PlanService.initialize() — reads the shared cache-duration portal setting once
  // resources are available, falling back to DEFAULT_CACHE_DURATION until then.
  private initialize(): void {
    if (this.cacheDuration !== this.DEFAULT_CACHE_DURATION) {
      return;
    }
    const cacheDurationValue = this.resourceService.getPortalSetting('Global_Site_Cache_Duration')?.Value;
    if (cacheDurationValue) {
      const parsedDuration = parseInt(cacheDurationValue, 10);
      if (!isNaN(parsedDuration) && parsedDuration > 0) {
        this.cacheDuration = parsedDuration * 60 * 1000; // convert minutes to milliseconds
      }
    }
  }

  getAddOns(
    distributionChannel: string,
    startDate: string,
    endDate: string,
    policyNumber: string
  ): Observable<AFXAddon[]> {
    return this.http.get<AFXAddon[]>(
      `${this.middlewareUrl}${this.accountPath}afx-get-addons?endDate=${endDate}&policyNumber=${policyNumber}&distributionChannel=${distributionChannel}&startDate=${startDate}`
    );
  }

  getCachedAddOns(
    distributionChannel: string,
    startDate: string,
    endDate: string,
    policyNumber: string
  ): Observable<AFXAddon[]> {
    const cacheKey = `${AppConstants.STORAGE_KEYS.AfxAddons}:${policyNumber}`;
    const cached = this.storageService.getLocal<AFXAddon[]>(cacheKey);
    if (cached) {
      return of(cached);
    }
    return this.getAddOns(distributionChannel, startDate, endDate, policyNumber).pipe(
      tap((data) => {
        if (data) {
          this.storageService.setLocal<AFXAddon[]>(cacheKey, data);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  clearAddOnsCache(policyNumber: string): void {
    const cacheKey = `${AppConstants.STORAGE_KEYS.AfxAddons}:${policyNumber}`;
    this.storageService.removeLocal(cacheKey);
  }

  private renewalCacheKey(planNumber: string): string {
    return `${AppConstants.STORAGE_KEYS.AfxRenewal}:${planNumber}`;
  }

  private fetchAfxRenewal(planNumber: string): Observable<AfxRenewalResponse> {
    return this.http.get<AfxRenewalResponse>(`${this.middlewareUrl}${this.accountPath}afx-renewal?planNumber=${planNumber}`);
  }

  // Session-storage + CacheEntry TTL cache, mirroring PlanService.getPlanDetails: short-circuit
  // on a still-valid cached entry unless refresh is forced, otherwise fetch, cache, and share.
  getAfxRenewal(planNumber: string, refresh?: boolean | null): Observable<AfxRenewalResponse> {
    this.initialize();
    const cacheKey = this.renewalCacheKey(planNumber);
    const cacheEntry = this.storageService.getSession<CacheEntry<AfxRenewalResponse>>(cacheKey);

    if (cacheEntry && refresh !== true && CacheEntry.isCacheValid(cacheEntry.timestamp, this.cacheDuration)) {
      return of(cacheEntry.data);
    }

    const fallback: AfxRenewalResponse = {
      policy: { policyNumber: planNumber },
      renewalStatus: '',
      isOfferAvailable: false,
      autoRenewConsent: false,
      selectedOffer: null,
    };
    return this.fetchAfxRenewal(planNumber).pipe(
      tap((afxRenewal) => {
        if (afxRenewal) {
          this.storageService.setSession<CacheEntry<AfxRenewalResponse>>(cacheKey, new CacheEntry(afxRenewal));
        }
      }),
      catchError(() => of(fallback)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Same request as `getAfxRenewal`, but propagates HTTP failures instead of
   * swallowing them into a display-safe fallback. Use this when the caller
   * needs to know a refresh actually failed (e.g. to roll back an optimistic
   * update) — `getAfxRenewal`'s fallback would otherwise be silently treated
   * as ground truth. On success, still refreshes the session cache (with the
   * same TTL entry shape `getAfxRenewal` reads) so a subsequent `getAfxRenewal`
   * call doesn't serve a stale pre-refresh value.
   */
  getAfxRenewalStrict(planNumber: string): Observable<AfxRenewalResponse> {
    this.initialize();
    const cacheKey = this.renewalCacheKey(planNumber);
    return this.fetchAfxRenewal(planNumber).pipe(
      tap((afxRenewal) => {
        if (afxRenewal) {
          this.storageService.setSession<CacheEntry<AfxRenewalResponse>>(cacheKey, new CacheEntry(afxRenewal));
        }
      })
    );
  }

  clearAfxRenewalCache(planNumber: string): void {
    this.storageService.removeSession(this.renewalCacheKey(planNumber));
  }

  updateAutoRenewalStatus(planNumber: string, autoRenewConsent: boolean): Observable<AfxRenewalConsentUpdateResponse> {
    return this.http.post<AfxRenewalConsentUpdateResponse>(
      `${this.middlewareUrl}${this.accountPath}afx-renewal-consent`,
      { planNumber, autoRenewConsent }
    );
  }

  initAfxSession(policyNumber: string): Observable<AfxSessionInitResponse> {
    return this.http.post<AfxSessionInitResponse>(`${this.middlewareUrl}${this.accountPath}afx-session-init?planNumber=${policyNumber}`, null);
  }
}
